import React, { useState, useEffect } from 'react';
import { CustomField } from '../../types/configTemplate';
import { useI18n } from '../../contexts/I18nContext';
import './CustomFieldModal.css';

interface CustomFieldModalProps {
  isOpen: boolean;
  field: CustomField | null;  // null = add new, object = edit existing
  onClose: () => void;
  onSave: (field: CustomField) => void;
  existingNames: string[];  // For validation (no duplicate names)
}

export const CustomFieldModal: React.FC<CustomFieldModalProps> = ({
  isOpen,
  field,
  onClose,
  onSave,
  existingNames
}) => {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [group, setGroup] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Initialize form when field changes (edit mode)
  useEffect(() => {
    if (field) {
      setName(field.name);
      setValue(String(field.value));
      setGroup(field.group || '');
      setNotes(field.notes || '');
    } else {
      setName('');
      setValue('');
      setGroup('');
      setNotes('');
    }
    setError(null);
  }, [field, isOpen]);

  const handleSave = () => {
    // Validate name
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    // Check for duplicate names (excluding current field in edit mode)
    const isEditing = field !== null;
    const namesToCheck = isEditing
      ? existingNames.filter(n => n !== field.name)
      : existingNames;

    if (namesToCheck.includes(name.trim())) {
      setError('A field with this name already exists');
      return;
    }

    // Validate name format (alphanumeric + underscore)
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name.trim())) {
      setError('Name must start with letter and contain only letters, numbers, underscores');
      return;
    }

    onSave({
      name: name.trim(),
      value: value,  // Allow empty value
      group: group.trim() || undefined,
      notes: notes.trim() || undefined
    });
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay custom-field-overlay" onClick={onClose}>
      <div className="custom-field-modal" onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className="modal-header">
          <h3>{field ? 'Edit Field' : 'Add Custom Field'}</h3>
          <button className="close-button" onClick={onClose} type="button" aria-label={t('common.close')}>×</button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="modal-content">
          <div className="form-group">
            <label htmlFor="field-name">Name *</label>
            <input
              id="field-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., custom_value1"
              disabled={field !== null}  // Cannot change name when editing
              autoFocus={field === null}
            />
            <span className="field-hint">Config key (letters, numbers, underscores)</span>
          </div>

          <div className="form-group">
            <label htmlFor="field-value">{t('common.value')}</label>
            <input
              id="field-value"
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder="e.g., allyurbasesbelongtous"
              autoFocus={field !== null}
            />
          </div>

          <div className="form-group">
            <label htmlFor="field-group">{t('common.group')}</label>
            <input
              id="field-group"
              type="text"
              value={group}
              onChange={e => setGroup(e.target.value)}
              placeholder="e.g., Debug, Network, Custom"
            />
            <span className="field-hint">{t('customFields.organizationalPurposes')}</span>
          </div>

          <div className="form-group">
            <label htmlFor="field-notes">{t('common.notes')}</label>
            <textarea
              id="field-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={t('customFields.descriptionPlaceholder')}
              rows={2}
            />
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} type="button">
            {t('common.cancel')}
          </button>
          <button className="btn btn-success" onClick={handleSave} type="button">
            {field ? t('common.save') : t('deviceTypes.addField')}
          </button>
        </div>
      </div>
    </div>
  );
};
